/**
 * Help Center. Layout: outputs/help-center-layout-v2-mockup-20261030.html (Bill 2026-10-30): plain, no
 * boxed callouts or accent stripes; topics read like a table of contents. Earlier mockup 2026-10-29:
 * home with videos first, then topics; articles in MyCertRenewal's shape (crumb, title, Applies to
 * and last updated, summary, video, numbered steps with a screenshot, Good to know, related).
 * Content ships in code (src/help/content.ts), the same for every company.
 */
import { Fragment, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Link, useParams } from "react-router-dom";
import { FEATURED, POPULAR, TOPICS, articleById, articleText, dur, posterSrc, searchHelp, shotSrc, thumbSrc, videoById, videoSrc, visibleArticles, type Block, type HelpArticle } from "../../help/content";
import { useStore } from "../../lib/store";
import { PlayIcon, VideoModal } from "../../components/SeeHow";
import { Icon } from "../../components/Icon";

/** **bold** to <b>; nothing else. */
function rich(s: string): ReactNode {
  return s.split(/(\*\*[^*]+\*\*)/g).map((x, i) => (x.startsWith("**") && x.endsWith("**") ? <b key={i}>{x.slice(2, -2)}</b> : <Fragment key={i}>{x}</Fragment>));
}
const topicOf = (id: string) => TOPICS.find((t) => t.id === id);

const slug = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
const jump = (id: string) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });

function Featured({ id, n, why, onPlay }: { id: string; n: number; why: string; onPlay: (id: string) => void }) {
  const v = videoById(id)!;
  return (
    <button type="button" className="hc-ft" onClick={() => onPlay(v.id)} aria-label={`Play: ${v.title} (${dur(v.secs)})`}>
      <span className="hc-ft-img"><img src={thumbSrc(v.id)} alt="" loading="lazy" /><span className="hc-ft-play"><PlayIcon size={18} /></span></span>
      <span className="hc-ft-meta"><span className="hc-ft-n" aria-hidden="true">{n}</span>Video · {dur(v.secs)}</span>
      <b>{v.title}</b><span className="hc-ft-why">{why}</span>
    </button>
  );
}

const PlayRing = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true"><circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" strokeWidth="1.8" /><path d="M10 8.5v7l5.5-3.5z" fill="currentColor" /></svg>
);
const PageIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" /><path d="M14 3v5h5" /><path d="M9 13h6M9 17h4" /></svg>
);

/** One row in a topic: the same shape for videos and articles. */
function Row({ a }: { a: HelpArticle }) {
  const v = a.video ? videoById(a.video) : undefined;
  return (
    <Link className="hc-row" to={`/help/${a.id}`}>
      {a.videos ? <span className="hc-k hc-k-v"><PlayRing /> {a.videos.length} videos</span>
        : v ? <span className="hc-k hc-k-v"><PlayRing /> Video {dur(v.secs)}</span> : <span className="hc-k hc-k-a"><PageIcon /> Article</span>}
      <b>{a.title}</b><span className="hc-who">{a.applies}</span><span className="hc-go" aria-hidden="true">›</span>
    </Link>
  );
}

export function HelpHome() {
  const [q, setQ] = useState("");
  const [playing, setPlaying] = useState<string | null>(null);
  const admin = useStore().p.anyAdmin;
  const ARTICLES = visibleArticles(admin);
  const hits = useMemo(() => searchHelp(q, admin), [q, admin]);
  const topics = TOPICS.filter((t) => ARTICLES.some((a) => a.topic === t.id));
  const [cur, setCur] = useState(topics[0]?.id ?? "");
  const picked = useRef(0); // a click on the topic list wins over scrolling for a moment
  useEffect(() => { document.title = "Help Center · DocVault"; return () => { document.title = "DocVault"; }; }, []);
  // The topic list shows which topic you're reading.
  useEffect(() => {
    const els = topics.map((t) => document.getElementById(`tp-${t.id}`)).filter((e): e is HTMLElement => !!e);
    const ob = new IntersectionObserver((es) => {
      const seen = es.filter((e) => e.isIntersecting).sort((x, y) => x.boundingClientRect.top - y.boundingClientRect.top)[0];
      if (seen && Date.now() - picked.current > 1200) setCur(seen.target.id.slice(3));
    }, { rootMargin: "-90px 0px -60% 0px" });
    els.forEach((e) => ob.observe(e));
    return () => ob.disconnect();
  }, [q]);
  return (
    <div className="view hc">
      <header className="hc-hh">
        <div className="eyebrow">Help Center</div>
        <h1 className="hc-t">Learn DocVault, <em>one task at a time</em></h1>
        <p className="hc-lede">Short answers and videos under a minute, recorded from DocVault itself.</p>
        <label className="hc-big">
          <Icon name="search" size={19} />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search: acknowledge, SOC 2, onboarding, two-step" aria-label="Search help" />
          {q && <button type="button" className="linkbtn" onClick={() => setQ("")}>Clear</button>}
        </label>
        {!q.trim() && <div className="hc-pop"><span>People often look for</span>{POPULAR.map((p) => <button key={p} type="button" onClick={() => setQ(p)}>{p}</button>)}</div>}
      </header>

      {q.trim() ? (
        <section className="hc-results" aria-label="Search results">
          <div className="hc-band"><h2>{hits.length ? `${hits.length} article${hits.length === 1 ? "" : "s"} for "${q.trim()}"` : `Nothing for "${q.trim()}"`}</h2>
            {!hits.length && <span>Try fewer words, or browse the topics below.</span>}</div>
          {hits.length > 0 && <div className="hc-rows">{hits.map((a) => <Row key={a.id} a={a} />)}</div>}
        </section>
      ) : null}

      <div className="hc-band"><h2>Start here</h2><span>Three videos that cover most of what people do.</span></div>
      <div className="hc-ftrow">{FEATURED.map((f, i) => <Featured key={f.video} id={f.video} n={i + 1} why={f.why} onPlay={setPlaying} />)}</div>

      <div className="hc-band"><h2>Browse by topic</h2><span>Every article, grouped the way the app is. Videos are marked.</span></div>
      <div className="hc-browse">
        <nav className="hc-toc" aria-label="Topics">
          <div className="hc-toc-h">Topics</div>
          {topics.map((t) => (
            <button key={t.id} type="button" className={cur === t.id ? "on" : ""} aria-current={cur === t.id ? "true" : undefined} onClick={() => { picked.current = Date.now(); setCur(t.id); jump(`tp-${t.id}`); }}>
              {t.title}<span>{ARTICLES.filter((a) => a.topic === t.id).length}</span>
            </button>
          ))}
        </nav>
        <div className="hc-tps">
          {topics.map((t) => {
            const list = ARTICLES.filter((a) => a.topic === t.id);
            return (
              <section key={t.id} id={`tp-${t.id}`} className="hc-tp" aria-labelledby={`tph-${t.id}`}>
                <div className="hc-tp-h"><h3 id={`tph-${t.id}`}>{t.title}</h3><span>{list.length} article{list.length === 1 ? "" : "s"}</span></div>
                <p className="hc-tp-d">{t.desc}</p>
                <div className="hc-rows">{list.map((a) => <Row key={a.id} a={a} />)}</div>
              </section>
            );
          })}
        </div>
      </div>
      <p className="hc-foot"><b>Still stuck?</b> Ask your company admin. Each article says who it's for, so some may not apply to you yet.</p>
      {playing && <VideoModal id={playing} onClose={() => setPlaying(null)} />}
    </div>
  );
}

function BlockView({ b }: { b: Block }) {
  if ("h" in b) return <h2 id={`s-${slug(b.h)}`}>{b.h}</h2>;
  if ("p" in b) return <p>{rich(b.p)}</p>;
  if ("ol" in b) return <ol className="hc-steps">{b.ol.map((x, i) => <li key={i}>{rich(x)}</li>)}</ol>;
  if ("ul" in b) return <ul>{b.ul.map((x, i) => <li key={i}>{rich(x)}</li>)}</ul>;
  return <figure className="hc-shot"><img src={shotSrc(b.shot)} alt={b.cap} loading="lazy" /><figcaption>{b.cap}</figcaption></figure>;
}

/** About 200 words a minute, at least 1. */
const readMins = (a: HelpArticle) => Math.max(1, Math.round(articleText(a).split(/\s+/).length / 200));

/** One part of a walkthrough: the player and its chapters. Clicking a chapter jumps there; the playing one is marked. */
function PartPlayer({ id }: { id: string }) {
  const v = videoById(id);
  const ref = useRef<HTMLVideoElement>(null);
  const [t, setT] = useState(0);
  if (!v) return null;
  const chs = v.chapters ?? [];
  const cur = chs.reduce((k, c, i) => (t >= c[0] ? i : k), 0);
  const go = (s: number) => { const el = ref.current; if (!el) return; el.currentTime = s; void el.play().catch(() => undefined); };
  return (
    <>
      <h2 id={`s-${slug(v.title)}`}>{v.title} <span className="hc-len">{dur(v.secs)}</span></h2>
      <figure className="hc-player hc-player-mat">
        <video ref={ref} src={videoSrc(v.id)} poster={posterSrc(v.id)} controls playsInline preload="none" aria-label={`Video: ${v.title}`} onTimeUpdate={(e) => setT(e.currentTarget.currentTime)} />
      </figure>
      {chs.length > 0 && <>
        <div className="hc-chh">Chapters</div>
        <div className="hc-chs">{chs.map(([s, name], i) => (
          <button key={s} type="button" className={`hc-ch ${t > 0 && i === cur ? "on" : ""}`} aria-current={t > 0 && i === cur ? "true" : undefined} onClick={() => go(s)}>
            <span className="hc-ts">{dur(s)}</span>{name}</button>
        ))}</div>
      </>}
    </>
  );
}

export function HelpArticlePage() {
  const { id = "" } = useParams();
  const admin = useStore().p.anyAdmin;
  const ARTICLES = visibleArticles(admin);
  const a: HelpArticle | undefined = articleById(id);
  useEffect(() => { window.scrollTo(0, 0); if (a) document.title = `${a.title} · DocVault Help`; return () => { document.title = "DocVault"; }; }, [id]);
  if (!a) return (
    <div className="view hc"><div className="card empty"><h3>That article isn't here</h3>It may have moved. <Link className="doclink" to="/help">Go to the Help Center</Link></div></div>
  );
  if (a.admin && !admin) return (
    <div className="view hc"><div className="card empty"><h3>This walkthrough is for company admins</h3>Ask your company admin if you need it. The short version, <Link className="doclink" to="/help/quarterly-review">Run a quarterly review, start to finish</Link>, covers the main steps.</div></div>
  );
  const t = topicOf(a.topic);
  const v = a.video ? videoById(a.video) : undefined;
  const parts = (a.videos ?? []).map(videoById).filter((x): x is NonNullable<typeof x> => !!x);
  // Articles without their own headings get one, so "On this page" always has something to point at.
  const body: Block[] = !a.body[0] || "h" in a.body[0] ? a.body : [{ h: "Steps" }, ...a.body];
  const heads = [...parts.map((p) => p.title), ...body.filter((b): b is { h: string } => "h" in b).map((b) => b.h), ...(a.good?.length ? ["Good to know"] : [])];
  const more = ARTICLES.filter((x) => x.topic === a.topic && x.id !== a.id);
  const related = (a.related ?? []).map(articleById).filter((x): x is HelpArticle => !!x && x.topic !== a.topic);
  return (
    <div className="view hc">
      <div className="hc-art-wrap">
        <article className="hc-art">
          <div className="hc-crumb"><Link to="/help">Help Center</Link><span>/</span>{t?.title}</div>
          <h1 className="hc-at">{a.title}</h1>
          <div className="hc-meta">For {/^[A-Z][a-z]+[A-Z]/.test(a.applies) ? a.applies : a.applies.charAt(0).toLowerCase() + a.applies.slice(1)} · Updated {a.updated} · {parts.length ? `${parts.length} videos, about ${Math.round(parts.reduce((n, p) => n + p.secs, 0) / 60)} minutes` : `${readMins(a)} minute read`}</div>
          <p className="hc-lead">{rich(a.summary)}</p>
          {v && <figure className="hc-player">
            <video src={videoSrc(v.id)} poster={posterSrc(v.id)} controls playsInline preload="none" aria-label={`Video: ${v.title}`} />
            <figcaption>Video, {v.secs} seconds, narrated, with captions on screen.</figcaption>
          </figure>}
          {parts.map((p) => <PartPlayer key={p.id} id={p.id} />)}
          {body.map((b, i) => <BlockView key={i} b={b} />)}
          {a.good?.length ? <section className="hc-good" aria-labelledby="s-good-to-know"><h2 id="s-good-to-know">Good to know</h2><ul>{a.good.map((x, i) => <li key={i}>{rich(x)}</li>)}</ul></section> : null}
        </article>
        <aside className="hc-rail" aria-label="More help">
          {heads.length > 1 && <><div className="hc-rail-h">On this page</div>{heads.map((h) => <button key={h} type="button" onClick={() => jump(`s-${slug(h)}`)}>{h}</button>)}</>}
          {more.length > 0 && <><div className="hc-rail-h">More in {t?.title}</div>{more.map((x) => <Link key={x.id} to={`/help/${x.id}`}>{x.title}</Link>)}</>}
          {related.length > 0 && <><div className="hc-rail-h">Related</div>{related.map((x) => <Link key={x.id} to={`/help/${x.id}`}>{x.title}</Link>)}</>}
          <div className="hc-rail-h">Help Center</div><Link to="/help">All topics and videos</Link>
        </aside>
      </div>
    </div>
  );
}
